const MEASUREMENT_ID = "G-TWR9478FZ5";
const ALLOWED_HOSTS = new Set(["lemce.com", "www.lemce.com"]);
const INR = "INR";

const CATEGORY_LABELS = {
  "mens-innerwear": "Men's Innerwear",
  "winter-socks": "Winter Socks",
  "winter-footwear": "Winter Footwear",
  "men-s-footwear": "Men's Footwear",
  essentials: "Everyday Essentials",
  caps: "Caps",
  socks: "Socks",
  "winter-slippers": "Winter Slippers",
};

export function isAnalyticsHost() {
  return typeof window !== "undefined" && ALLOWED_HOSTS.has(window.location.hostname.toLowerCase());
}

// gtag.js loads once, early, from public/index.html <head> (hostname-guarded).
// initAnalytics is only a fallback if that snippet is somehow missing.
let loading;
export function initAnalytics() {
  if (!isAnalyticsHost()) return Promise.resolve(false);
  if (typeof window.gtag === "function") return Promise.resolve(true);
  if (loading) return loading;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", MEASUREMENT_ID, { send_page_view: false }); // router owns page views
  loading = new Promise((resolve) => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
  return loading;
}

export function gaEvent(name, params = {}) {
  if (!isAnalyticsHost()) return;
  initAnalytics();
  if (typeof window.gtag !== "function") return;
  window.gtag("event", name, params);
}

const categoryLabel = (slug) => {
  if (!slug) return undefined;
  return CATEGORY_LABELS[slug] || slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

export const gaItem = ({ id, name, price, quantity = 1, brand = "LEMCE", category, variant }) => ({
  item_id: String(id),
  item_name: name,
  price: Number(price),
  quantity: Number(quantity),
  item_brand: brand,
  ...(category && { item_category: categoryLabel(category) }),
  ...(variant && { item_variant: variant }),
});

const total = (items) => items.reduce((s, x) => s + Number(x.price) * Number(x.quantity || 1), 0);

const cartGaItems = (cartItems) =>
  cartItems.map((i) =>
    gaItem({
      id: i.sku || i.slug,
      name: i.name,
      price: i.price,
      quantity: i.qty,
      category: i.category,
      variant: [i.color, i.size].filter(Boolean).join(" / "),
    }),
  );

export function trackPageView(path) {
  if (!isAnalyticsHost()) return;
  gaEvent("page_view", { page_title: document.title, page_location: window.location.href, page_path: path });
}

export function trackViewItemList(products, listName, listId) {
  const items = products
    .slice(0, 50)
    .map((p, idx) => ({ ...gaItem({ id: p.sku || p.slug, name: p.name, price: p.price, category: p.category }), index: idx }));
  if (!items.length) return;
  gaEvent("view_item_list", { item_list_id: listId, item_list_name: listName, items });
}

export function trackViewItem(product) {
  const item = gaItem({ id: product.sku || product.slug, name: product.name, price: product.price, category: product.category });
  gaEvent("view_item", { currency: INR, value: total([item]), items: [item] });
}

export function trackAddToCart(product, quantity = 1, variant) {
  const item = gaItem({ id: product.sku || product.slug, name: product.name, price: product.price, quantity, category: product.category, variant });
  gaEvent("add_to_cart", { currency: INR, value: total([item]), items: [item] });
}

export function trackViewCart(cartItems) {
  const items = cartGaItems(cartItems);
  if (!items.length) return;
  gaEvent("view_cart", { currency: INR, value: total(items), items });
}

export function trackBeginCheckout(cartItems, coupon) {
  const items = cartGaItems(cartItems);
  if (!items.length) return;
  gaEvent("begin_checkout", { currency: INR, value: total(items), items, ...(coupon && { coupon }) });
}

export function trackAddPaymentInfo(cartItems, paymentType) {
  const items = cartGaItems(cartItems);
  if (!items.length) return;
  gaEvent("add_payment_info", { currency: INR, value: total(items), payment_type: paymentType, items });
}

export function trackPurchase(order) {
  const guardKey = `ga_purchase_${order.order_id}`;
  try {
    if (sessionStorage.getItem(guardKey)) return; // never double-count a refresh
    sessionStorage.setItem(guardKey, "1");
  } catch (e) { /* storage unavailable */ }
  const items = cartGaItems(order.items || []);
  gaEvent("purchase", {
    transaction_id: String(order.order_id),
    value: Number(order.total),
    currency: INR,
    tax: Number(order.tax || 0),
    shipping: Number(order.shipping ?? order.shipping_charge ?? 0),
    ...(order.coupon_code && { coupon: order.coupon_code }),
    items,
  });
}
